import { Injectable, inject } from '@angular/core';

import { ILegPart, IOutcome } from '@frontend/oxygen/shared-utils';
import * as _ from 'underscore';

import { EnvironmentConfig, IConfiguration } from '../../../client-config/index';
import { PubSubService } from '../../../core/services/communication/pubsub/pubsub.service';
import { LocaleService } from '../../../core/services/locale/locale.service';
import { UserService } from '../../../core/services/user/user.service';
import { bma } from '../../../lazy-modules/locale/translations/en-US/bma.lang';
import { RoutingState } from '../../../shared/services/routingState/routing-state.service';
import { IBetSelection, IParams } from '../betSelection/bet-selection.model';
import { BetSelections } from './bet-selections';

@Injectable({ providedIn: 'root' })
export class BetSelectionsService extends BetSelections {
    static ngInjectableDef = undefined;
    protected readonly environment: IConfiguration = inject(EnvironmentConfig).configuration;
    protected userService = inject(UserService);
    protected routingState = inject(RoutingState);
    protected localeService = inject(LocaleService);
    isBrandLadbrokes: boolean;

    constructor(public override pubSubService: PubSubService) {
        super(pubSubService);
        this.isBrandLadbrokes = this.environment.BRAND === this.localeService.getString(bma.brands.ladbrokes).toLowerCase();
    }

    addSelection(selection: IBetSelection): void {
        this.selectionsData.push(selection);
        this.pubSubService.publishSync(this.pubSubService.API.BETSLIP_SELECTIONS_UPDATE, this.selectionsData);
        this.pubSubService.publish(this.pubSubService.API.FLAG_TRACK_ADD, 'betslipSelections');
        selection.eventIsLive && this.pubSubService.publish(this.pubSubService.API.FLAG_TRACK_ADD, 'of adds to slip');
        selection?.isFeatured && this.pubSubService.publish(this.pubSubService.API.FLAG_TRACK_ADD, 'featuredAddsToSlip');

        if (this.isBrandLadbrokes && this.userService.status && selection && selection.categoryId === '16') {
            this.pubSubService.publish(this.pubSubService.API.FLAG_TRACK_ADD, 'totalFootballAddsToSlip');
            if (selection.eventIsLive) {
                this.pubSubService.publish(this.pubSubService.API.FLAG_TRACK_ADD, 'totalInplayFootballAddToSlip');

                const isIPFootballPage = this.routingState.getCurrentUrl()?.includes('in-play/football');
                isIPFootballPage && this.pubSubService.publish(this.pubSubService.API.FLAG_TRACK_ADD, 'InplayFootballAddToSlip');
            }
        }
    }

    removeSelection(selection: IBetSelection): void {
        const index = this.selectionsData.indexOf(selection);
        this.selectionsData.splice(index, 1);
        this.pubSubService.publishSync(this.pubSubService.API.BETSLIP_SELECTIONS_UPDATE, this.selectionsData);
    }

    /**
     * @param {IBetSelection[]} selections
     * @memberof BetSelectionsService
     */
    removeMultiSelection(selections: IBetSelection[]): void {
        _.each(selections, (selection) => {
            const index = this.selectionsData.indexOf(selection);
            this.selectionsData.splice(index, 1);
        });
        this.pubSubService.publishSync(this.pubSubService.API.BETSLIP_SELECTIONS_UPDATE, this.selectionsData);
    }

    updateSelection(updatedSelection: IBetSelection) {
        const index = this.selectionsData.findIndex((selection) => updatedSelection.id.toString().includes(selection.id));
        this.selectionsData[index] = updatedSelection;
        this.pubSubService.publishSync(this.pubSubService.API.BETSLIP_SELECTIONS_UPDATE, this.selectionsData);
    }

    removeSelectionById(id: number): void {
        const selection: IBetSelection = _.findWhere(this.selectionsData, { id });
        this.removeSelection(selection);
    }

    getOutcome(outcomeId: string | number): IBetSelection | IOutcome {
        return this.selectionsData.reduce((result, sel) => {
            return _.find(sel.outcomes, { id: outcomeId }) || result;
        }, null);
    }

    findById(id: string, isLotto: boolean = false, priceId: string = ''): IBetSelection | undefined {
        let betFound: IBetSelection | undefined;

        if (id.includes('SCORECAST')) {
            if (this.selectionsData.length) {
                const filteredBet = _.filter(this.selectionsData, (selection) => {
                    return _.every(id.split('|'), (item: string) => selection.id.includes(item));
                });
                betFound = filteredBet[0];
            }
        } else if (isLotto) {
            betFound = this.selectionsData.find((selection) => id.includes(selection.id) && priceId === selection.details.priceId);
        } else {
            betFound = _.find(this.selectionsData, { id });
        }
        if (!betFound) {
            _.forEach(this.selectionsData, (selection: IBetSelection) => {
                if (selection.id == id || (selection.params && selection.params.id === id)) {
                    betFound = selection;
                }
            });
        }
        return betFound;
    }

    flush(): void {
        this.selectionsData = [];
        this.pubSubService.publishSync(this.pubSubService.API.BETSLIP_SELECTIONS_UPDATE, this.selectionsData);
    }

    count(): number {
        return this.selectionsData.length;
    }

    mapParsed(params: IParams): IBetSelection | undefined {
        const type = this.getBetType(params);
        const id = `${type}|${params.outcomes.map((outcome: IOutcome) => outcome.id).join('|')}`;
        return this.findById(id);
    }

    private getBetType(params: IParams): string {
        const type = params.combi || 'SGL';

        if (/^(FORE|TRI)CAST$/.test(type) && params.legParts.some((part: ILegPart) => part.places === '*')) {
            return `${type}_COM`;
        }

        return type;
    }
}
